import React from "react";
//#region types
import { MenuButtonProps, MoreActionMenuItem, ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
//#endregion
//#region components
import { Grid, MenuItem, Divider, ListItemIcon, ListItemText, Paper, ClickAwayListener, Popper } from "@mui/material";
import BlurBackdrop from "../blurBackdrop";
import useMenuButtonClick from "./useMenuButtonClick";
import PopMenuEvents from "../menu/popMenuEvents";
import Icon from "../icon";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { CSSHelper, LocalizationHelperInstance } from "utils/helpers";

const MenuButton: React.FC<MenuButtonProps> = (props) => {
  const { classes, contentOnOpen, button, disableSuccessMessages, blurBackgroundOnOpen } = props;

  const menuItems = props.menuItems.filter((menuItem) => typeof menuItem !== "object" || !menuItem.hidden);

  const onMenuItemClick = useMenuButtonClick(props.onActionSuccess, props.onError);
  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
    closeMenu: () => void
  ) => {
    event.persist();
    const menuItem = menuItems[index] as MoreActionMenuItem;
    onMenuItemClick(menuItem, disableSuccessMessages ?? false, event);

    closeMenu();
  };

  return menuItems.length > 0 ? (
    <PopMenuEvents
      allowClickEventPropagation={props.allowClickEventPropagation}
      menuStateChangeCallback={props.menuStateChangeCallback}
      render={(anchorEl, isOpen, openMenu, closeMenu, isClosing) => (
        <>
          <BlurBackdrop show={blurBackgroundOnOpen && isOpen} />
          <ClickAwayListener mouseEvent="onMouseDown" onClickAway={() => closeMenu()}>
            <Grid
              item
              className={clsx(classes?.root, isOpen ? classes?.menuOpen : "", isClosing ? classes?.menuClosing : "")}
            >
              <button.type
                {...button.props}
                onClick={isOpen ? closeMenu : openMenu}
                disabled={button.props.disabled || menuItems.length === 0}
                className={clsx(button.props.className, cssExports.menuButton)}
                hovertext={!isOpen && "hoverText" in button.props ? button.props.hoverText : undefined}
              >
                {contentOnOpen && isOpen ? contentOnOpen : button.props.children}
              </button.type>
              <Popper
                placement={
                  props.menuPlacement === "bottom"
                    ? "bottom-end"
                    : props.menuPlacement === "top"
                      ? "top-end"
                      : props.menuPlacement
                }
                disablePortal={props.disablePopperPortal ?? true}
                modifiers={[
                  {
                    name: "flip",
                    options: {
                      enabled: true,
                    },
                  },
                  {
                    name: "preventOverflow",
                    options: {
                      enabled: true,
                      boundariesElement: "window",
                    },
                  },
                  {
                    name: "arrow",
                    options: {
                      enabled: true,
                    },
                  },
                  {
                    name: "offset",
                    options: {
                      offset:
                        props.popperOffSet ??
                        (props.menuPlacement === "bottom" ? [0, 0] : props.menuPlacement === "top" ? [0, 20] : [0, 5]),
                    },
                  },
                ]}
                anchorEl={anchorEl}
                open={isOpen}
                className={cssExports.popoverContainerOuter}
              >
                <Paper className={clsx(cssExports.popoverContainer, cssExports.actionsPopover, classes?.menu)}>
                  {menuItems.map((item, index) => {
                    return item === ACTION_SEPARATOR ? (
                      <Divider key={index}></Divider>
                    ) : typeof item === "string" ? (
                      <label key={item} className={cssExports.menuTitle}>
                        {LocalizationHelperInstance.getFormattedMessage(item, item)}
                      </label>
                    ) : (
                      <MenuItem
                        id={item.menuKey ? `menu-item-${item.menuKey}` : undefined}
                        key={item.description}
                        onClick={(e) => handleMenuItemClick(e, index, closeMenu)}
                        className={clsx(cssExports.actionItem, CSSHelper.getClassByDisplayType(item.highlightType))}
                      >
                        <ListItemIcon className={cssExports.actionIcon}>
                          <Icon icon={item.icon} className={clsx(item.iconClassName)} />
                        </ListItemIcon>
                        <ListItemText primary={item.description} className={cssExports.actionItemText} />
                      </MenuItem>
                    );
                  })}
                </Paper>
              </Popper>
            </Grid>
          </ClickAwayListener>
        </>
      )}
    />
  ) : (
    <></>
  );
};

export default MenuButton;
